import { use } from 'react';
import {
  Accordion,
  Table,
  Text,
  Stack,
  Paper,
  Title,
  Code,
  Alert,
  Group,
  SimpleGrid,
  ThemeIcon,
  Badge,
} from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { LoansContext } from '@/context/loans-context-definition';
import { getEligiblePlans } from '@/utils/paymentPlans/getEligiblePlans';
import { PlanCriterionResult } from '@/types/eligibilityTypes';

import { formatCurrency } from '@/utils/formatters';
import { PlanType, PLAN_LABELS } from '@/types/planTypes';

function KeyValue({
  label,
  value,
}: {
  label: string;
  value: string | number | boolean | null | undefined;
}) {
  const displayValue = value === null || value === undefined ? 'N/A' : String(value);
  return (
    <Group gap="xs">
      <Text size="sm" fw={500}>
        {label}:
      </Text>
      <Code>{displayValue}</Code>
    </Group>
  );
}

export function EligibilityDebugger() {
  const context = use(LoansContext);

  if (!context) {
    return <Alert color="red">Loan context is not available.</Alert>;
  }

  const { surveyData, filteredLoans, meetsFinancialHardship } = context;

  if (!surveyData) {
    return <Alert color="red">Survey data is missing from context.</Alert>;
  }

  if (filteredLoans.length === 0) {
    return <Alert color="yellow">No loans available to analyze.</Alert>;
  }

  const {
    annualIncome,
    partnerAnnualIncome,
    state,
    familySize,
    /* maritalStatus, */ pslfEligible,
    filingStatus,
  } = surveyData;

  return (
    <Stack>
      <Paper shadow="none" p="md" mb="md">
        <Title order={4} mb="sm">
          User Information
        </Title>
        <SimpleGrid cols={{ base: 2, xs: 2, md: 3, xl: 4 }}>
          <KeyValue label="Annual Income" value={formatCurrency(annualIncome)} />
          <KeyValue label="Partner Income" value={formatCurrency(partnerAnnualIncome)} />
          <KeyValue label="State" value={state} />
          <KeyValue label="Family Size" value={familySize} />
          <KeyValue label="Filing Status" value={filingStatus} />
          <KeyValue label="PSLF Eligible Input" value={pslfEligible} />
          <KeyValue label="Meets Financial Hardship (calculated)" value={meetsFinancialHardship} />
        </SimpleGrid>
      </Paper>

      <Title order={4} mb="sm">
        Loan Eligibility Analysis (Grouped by Plan)
      </Title>
      <Accordion variant="separated" multiple>
        {filteredLoans.map((loan) => {
          const eligibilityInput = {
            loan,
            allLoans: filteredLoans,
            income: annualIncome ?? 0,
            state: state ?? '',
            pslfEligible: pslfEligible,
            meetsFinancialHardship: meetsFinancialHardship,
          };
          let eligibilityCriteria: PlanCriterionResult[] = [];
          let error: string | null = null;
          try {
            const result = getEligiblePlans(eligibilityInput);
            if (Array.isArray(result)) {
              eligibilityCriteria = result;
            } else {
              error = 'Invalid eligibility criteria result';
            }
          } catch (e) {
            console.error(`Error getting eligible plans for loan ${loan['Loan Award ID']}:`, e);
            let errorMessage = 'Failed to calculate eligibility.';
            if (e instanceof Error) {
              errorMessage = e.message;
            }
            error = errorMessage;
          }

          const loanAwardId = loan['Loan Award ID'];
          const loanType = loan['Loan Type Description'] ?? 'Unknown Type';

          return (
            <Accordion.Item key={loanAwardId} value={loanAwardId}>
              <Accordion.Control>
                <Stack gap={0}>
                  <Text fw={500}>
                    Loan: {loanType} ({loanAwardId})
                  </Text>
                  <Group gap="xs" wrap="wrap">
                    <KeyValue label="Type Group" value={loan['Loan Type Group']} />
                    <KeyValue label="Loan Date" value={loan['Loan Date']} />
                    <KeyValue
                      label="Outstanding"
                      value={formatCurrency(loan['Total Outstanding'])}
                    />
                    <KeyValue
                      label="Consolidated w/ Parent PLUS"
                      value={
                        (loan['Consolidation Loan With Any Parent Plus Indicator'] as string | null)
                          ? (loan['Consolidation Loan With Any Parent Plus Indicator'] as string)
                          : 'n/a'
                      }
                    />
                    <KeyValue label="Status" value={loan['Current Loan Status Description']} />
                  </Group>
                </Stack>
              </Accordion.Control>
              <Accordion.Panel>
                {error && (
                  <Alert color="red" title="Calculation Error">
                    {error}
                  </Alert>
                )}
                {eligibilityCriteria.length > 0 && !error && (
                  <Accordion variant="contained" radius="xs">
                    {Object.values(PlanType).map((planType) => {
                      const planCriteriaForLoan = eligibilityCriteria.filter(
                        (c) => c.plan === planType
                      );
                      if (planCriteriaForLoan.length === 0) return null;

                      const isPlanEligible = !planCriteriaForLoan.some((c) => {
                        const blockingCriteria = [
                          'Loan Type Eligibility',
                          'SAVE Plan Availability (Court Injunction)',
                          'Minimum Debt Threshold ($30k Direct)',
                          'Partial Financial Hardship',
                          'PAYE New Borrower Requirements',
                          'PSLF Eligibility Indicated',
                          'PSLF Loan Type Eligibility (Direct Required)',
                        ];
                        return blockingCriteria.includes(c.criterion) && !c.isMet;
                      });

                      return (
                        <Accordion.Item key={planType} value={planType}>
                          <Accordion.Control>
                            <Group justify="space-between" maw={300}>
                              <Text fw={500} size="sm">
                                {PLAN_LABELS[planType]}
                              </Text>
                              <Badge color={isPlanEligible ? 'teal' : 'red'} variant="light">
                                {isPlanEligible ? 'Eligible' : 'Ineligible'}
                              </Badge>
                            </Group>
                          </Accordion.Control>
                          <Accordion.Panel>
                            <Table highlightOnHover withTableBorder withColumnBorders fz="xs">
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th>Criterion</Table.Th>
                                  <Table.Th>Met?</Table.Th>
                                  <Table.Th>Explanation (Friendly)</Table.Th>
                                  <Table.Th>Explanation (Technical)</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {planCriteriaForLoan.map((criterionResult, index) => (
                                  <Table.Tr key={`${criterionResult.criterion}-${String(index)}`}>
                                    <Table.Td>{criterionResult.criterion}</Table.Td>
                                    <Table.Td>
                                      {criterionResult.isMet ? (
                                        <ThemeIcon color="green" size="sm">
                                          <IconCheck size={14} />
                                        </ThemeIcon>
                                      ) : (
                                        <ThemeIcon color="red" size="sm">
                                          <IconX size={14} />
                                        </ThemeIcon>
                                      )}
                                    </Table.Td>
                                    <Table.Td>{criterionResult.explanationFriendly}</Table.Td>
                                    <Table.Td>
                                      <Code style={{ whiteSpace: 'pre-wrap', fontSize: '10px' }}>
                                        {criterionResult.technicalReason}
                                      </Code>
                                    </Table.Td>
                                  </Table.Tr>
                                ))}
                              </Table.Tbody>
                            </Table>
                          </Accordion.Panel>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </Stack>
  );
}
